import { put, cancelled, takeEvery, call } from 'redux-saga/effects';

import { isLoggedIn, getData, removeData } from '@/data/cache';
import {
	INIT_APP,
	INIT_APP_SUCCESS,
	INIT_APP_ERROR,
} from '@/redux/ducks/init/types';
import { SAVE_LOCAL_USER_INFO } from '@/redux/ducks/login/types';
import { SAVE_MERCHANT_INFO } from '@/redux/ducks/merchantProfile/types';
import { setPermission, setMerchantType } from '@/utils/permissions';
import Constants from '@/utils/constants';
import { filterUserGroupsByStatus } from '@/utils/helper';
import { GET_CHIMERA_CONFIG } from '@/redux/ducks/userConfig/types';
import { sendCustomAnalyticsEvent } from '@/utils/AnalyticsUtils';
import { APP_LINK_URL, LOGIN_API_SUCCESS } from '@/constants';
import router from '@/router/router';
import { usePFBRoutes } from '@/hooks/usePFBRoutes';
import { RoutePath } from '@/types';
import { isLoginRedirectFlow } from '@/utils/LoginRedirectionUtils';
import { pfbAnalyticsClient } from '@/data/analyticsClient';
import { normalizeFoxtrotEventData } from '@/utils/FoxtrotUtils';

import { getRolesDesc } from './userProfileSagas';
import {
	getUserPermissions,
	getUserInfo,
	getUserGroupList,
} from './userApiSaga';
import { fetchMerchantList, getMerchantProfile } from './merchantDetailsSaga';

/** Function to check valid urls
 * @params
 * path: string - the url user is navigating.
 */
export function isValidPath(path) {
	const pathWithoutParams = path.split('?')[0];
	return Constants.WHITELISTED_URl.map((url) => {
		const urlRegEx = new RegExp(`^${url}$`);
		return urlRegEx.test(pathWithoutParams);
	}).reduce((acc, currentVal) => !!(acc || currentVal), undefined);
}

/* Function to hydrate redux store  */
export function* getLocalUserInfo() {
	// Get the required data saved in localstorage.
	const selectedMID = yield call(getData, Constants.SELECTED_MID);
	const userPermissions = yield call(getData, Constants.USER_PERMISSIONS_KEY);
	const userInfo = yield call(getData, Constants.USER_INFO_KEY);
	const merchantProfile = yield call(getData, Constants.MERCHANT_PROFILE_KEY);
	const merchantList = yield call(getData, Constants.MERCHANT_LIST_KEY);
	const userGroupsList = yield call(getData, Constants.USER_GROUPS_LIST_KEY);
	const filteredUserGroupsList = filterUserGroupsByStatus(userGroupsList);
	if (userInfo && !merchantProfile) {
		throw new Error('App initialization failed.');
	} else if (userPermissions && userInfo) {
		yield call(setPermission, userPermissions);
		yield call(
			setMerchantType,
			merchantProfile ? merchantProfile.type : undefined,
		);
		// Save userRoles, userPermissions, userInfo data in userAuth key of redux store.
		yield put({
			type: SAVE_LOCAL_USER_INFO,
			userPermissions,
			userInfo,
			userGroupsList: filteredUserGroupsList,
			selectedMID,
		});
		// Save merchantProfile merchantList in merchantProfile key of redux store.
		yield put({
			type: SAVE_MERCHANT_INFO,
			merchantProfile,
			merchantList,
		});
	} else {
		throw new Error('App initialization failed.');
	}
}
export function* initializingApiCalls() {
	const selectedMID = yield call(getData, Constants.SELECTED_MID);
	const groupList = yield call(getData, Constants.USER_GROUPS_LIST_KEY);
	if (groupList === null) yield call(getUserGroupList); // get and save user group list in localstorage in USER_GROUP_LIST_KEY
	if (selectedMID !== null) {
		yield call(getUserPermissions); // Get all user's permission from Api and save in localstorage in USER_PERMISSIONS_KEY
		yield call(getUserInfo); // Get user information of logged-in user and save in localstorage in USER_INFO_KEY
		if (
			selectedMID.userGroupNamespace.groupName ===
				Constants.MERCHANT_GROUP_NAME ||
			selectedMID.userGroupNamespace.groupName ===
				Constants.SUPERMERCHANT_GROUP_NAME
		) {
			yield call(getMerchantProfile); // get and save merchant profile in localstorage in MERCHANT_PROFILE_KEY
		}
		pfbAnalyticsClient.flushQueuedEvent(normalizeFoxtrotEventData);
		if (
			selectedMID.userGroupNamespace.groupName ===
			Constants.SUPERMERCHANT_GROUP_NAME
		) {
			yield call(fetchMerchantList); // get and save merchant list in localstorage in MERCHANT_LIST_KEY
		}
		yield put({
			type: GET_CHIMERA_CONFIG,
			merchantId: selectedMID.userGroupNamespace.groupValue,
		});
		yield call(getRolesDesc, { admin: true });
	}
}

/* Hydrates redux store from localstorage and redirects to /login or /dashboard based on user state in redux store.
   For /set-password redirect to desired pages without user hydration.
*/
export function* initAppFlow() {
	let search = window.location.search;
	try {
		if (window.location.pathname === RoutePath.RESET_PASSWORD) {
			// User is navigated to reset password
			yield call(
				setTimeout,
				() => router.navigate(`/set-password${search}`),
				0,
			);
			yield put({
				type: INIT_APP_SUCCESS,
			});
		} else if (window.location.pathname === RoutePath.USER_VERIFY) {
			yield call(
				setTimeout,
				() => router.navigate(`${RoutePath.USER_VERIFY}${search}`),
				0,
			);
			yield put({ type: INIT_APP_SUCCESS });
		} else if (window.location.pathname === RoutePath.APPLINK) {
			// Redirecting user to appstore/playstore in case app is not installed on the mobile device
			// Note: This will redirect to business.phonepe.com/login in case user is not on mobile device
			window.location.href = APP_LINK_URL;
		} else {
			const isLoggedInUser = yield call(isLoggedIn, Constants.LOGIN_CHECK_KEY); // Check whether user was previously logged in or not.
			if (isLoggedInUser) {
				const isFirstLogin = !(yield call(getData, Constants.USER_INFO_KEY));
				yield call(initializingApiCalls); // Important to keep updated on refresh, Please don't remove
				// const userGroupsList = yield call(getData, Constants.USER_GROUPS_LIST_KEY);
				const selectedMID = yield call(getData, Constants.SELECTED_MID);
				if (isFirstLogin) {
					sendCustomAnalyticsEvent('custom', {
						name: LOGIN_API_SUCCESS,
					});
				}
				if (
					selectedMID &&
					[RoutePath.LOGIN_REDIRECTION, RoutePath.LOGIN].includes(
						window.location.pathname,
					) &&
					isLoginRedirectFlow()
				) {
					yield call(
						setTimeout,
						() => router.navigate('/login-redirection'),
						0,
					);
				} else if (selectedMID) {
					yield call(getLocalUserInfo);
					let redirectUrl = ''; // Initiate the intial path to dashboard.
					/* Get the url on which user was before auto logged out.
           The required Url is stored in DESTINATION_URL key in
           local storage. */

					const dest = yield call(getData, Constants.DESTINATION_URL);

					/* If there is any destination url is there on which user
           have to navigated on login the navigated path is replaced
           with destination path else redirected to dashboard */

					if (dest) {
						const destRouteData = usePFBRoutes.getData(dest.path);
						redirectUrl = destRouteData?.data.isAuthenticated ? dest.path : '';
						search = dest.search || '';
					} else if (
						window.location.pathname === RoutePath.LOGIN ||
						window.location.pathname === RoutePath.REGISTER ||
						window.location.pathname === RoutePath.PG_REGISTER
					) {
						/* This is the first login case where the older url will always be login and in this case
            we will send the user to dashboard */
						redirectUrl = RoutePath.DASHBOARD;
					} else {
						/* on every refresh we need to keep user on the same page where the person refreshed the page */
						redirectUrl = window.location.pathname;
					}
					/* Check if user have permission to access the path if
           he have required permission allow him to navigate else
           show a 404 page */
					yield call(() => router.navigate(redirectUrl));
					yield call(removeData, Constants.DESTINATION_URL);
					yield call(() => router.navigate({ pathname: redirectUrl, search }));
				} else {
					yield call(setTimeout, () => router.navigate(RoutePath.DASHBOARD), 0);
				}
			} else if (window.location.pathname === RoutePath.PG_REGISTER) {
				yield call(
					setTimeout,
					() => history.push({ pathname: '/pg/register', search }),
					0,
				);
			} else if (window.location.pathname === RoutePath.REGISTER) {
				yield call(
					setTimeout,
					() => router.navigate({ pathname: RoutePath.REGISTER, search }),
					0,
				);
			} else {
				/************************************************/
				yield call(setTimeout, () => router.navigate(RoutePath.LOGIN), 0);
			}
			yield put({
				type: INIT_APP_SUCCESS,
			});
		}
	} catch (error) {
		// Error handling
		yield put({
			type: INIT_APP_ERROR,
			error: error.data,
		});
		yield call(setTimeout, () => router.navigate(RoutePath.LOGIN), 0);
	} finally {
		if (yield cancelled()) {
			yield call(setTimeout, () => router.navigate(RoutePath.LOGIN), 0);
		}
	}
}

function* initAppWatcher() {
	yield takeEvery(INIT_APP, initAppFlow);
}

export default initAppWatcher;
